import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { BarChart3, Download, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const AthleticDirectorSOP = () => {
  const navigate = useNavigate();

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate("/dashboard/help")} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Help Center
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Athletic Director Guide</h1>
          </div>
          <p className="text-muted-foreground">
            Department oversight, analytics, revenue sharing management, and strategic NIL planning
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
            <CardDescription>
              As Athletic Director, you oversee department-wide NIL activities, manage revenue sharing pools (CAPS),
              ensure Title IX compliance, and provide strategic direction for your institution's NIL program.
            </CardDescription>
          </CardHeader>
        </Card>

        <Accordion type="single" collapsible className="space-y-4">
          <AccordionItem value="monitoring">
            <AccordionTrigger className="text-lg font-semibold">
              Monitoring Department-Wide NIL Activity
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Dashboard Overview</h4>
                <p className="text-muted-foreground">
                  Navigate to Dashboard → Analytics to view comprehensive NIL metrics:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Total Deal Volume:</strong> Aggregate value across all sports</li>
                  <li><strong>Active Deals by Sport:</strong> Distribution across teams</li>
                  <li><strong>Deal Status Breakdown:</strong> Pending, approved, rejected counts</li>
                  <li><strong>Top Athletes:</strong> Highest-earning student-athletes</li>
                  <li><strong>Brand Relationships:</strong> Most active sponsors and partners</li>
                </ul>

                <h4 className="font-semibold mt-4">Key Performance Indicators (KPIs)</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Average deal value by sport and gender</li>
                  <li>Deal approval rate and average review time</li>
                  <li>Compliance flag rate and resolution time</li>
                  <li>Revenue sharing pool utilization</li>
                  <li>Title IX equity variance</li>
                </ul>

                <h4 className="font-semibold mt-4">Monthly Review Process</h4>
                <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                  <li>Review analytics dashboard for trends and outliers</li>
                  <li>Check Title IX compliance metrics</li>
                  <li>Analyze deal distribution across sports and genders</li>
                  <li>Identify potential issues or opportunities</li>
                  <li>Meet with compliance officers to discuss findings</li>
                  <li>Adjust policies or allocations as needed</li>
                </ol>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="reports">
            <AccordionTrigger className="text-lg font-semibold">
              Generating Compliance Reports for NCAA
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Required NCAA Reports</h4>
                <p className="text-muted-foreground">
                  Navigate to Reports & Export to generate official NCAA submissions:
                </p>

                <h4 className="font-semibold mt-4">1. NIL Activity Summary Report</h4>
                <p className="text-muted-foreground">
                  Quarterly report including:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Total number of deals by quarter</li>
                  <li>Aggregate deal value by sport</li>
                  <li>Number of participating athletes</li>
                  <li>Top deal categories</li>
                </ul>

                <h4 className="font-semibold mt-4">2. Title IX Equity Report</h4>
                <p className="text-muted-foreground">
                  Annual report demonstrating:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Gender participation rates</li>
                  <li>Revenue sharing distribution by gender</li>
                  <li>Variance analysis and corrective actions</li>
                </ul>

                <h4 className="font-semibold mt-4">3. Institutional Sponsor Conflict Report</h4>
                <p className="text-muted-foreground">
                  Report tracking:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Number of conflicts identified</li>
                  <li>Resolution outcomes</li>
                  <li>Categories with highest conflict rates</li>
                </ul>

                <h4 className="font-semibold mt-4">Generating Reports</h4>
                <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                  <li>Navigate to Reports & Export</li>
                  <li>Select report type and date range</li>
                  <li>Apply filters (sport, gender, etc.)</li>
                  <li>Preview report data</li>
                  <li>Export as PDF or Excel</li>
                  <li>Submit to NCAA portal by deadlines</li>
                </ol>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="caps">
            <AccordionTrigger className="text-lg font-semibold">
              Managing Revenue Sharing Pools (CAPS)
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Understanding CAPS</h4>
                <p className="text-muted-foreground">
                  College Athlete Payment System (CAPS) allows institutions to distribute revenue directly to athletes
                  through sport-specific pools, separate from individual NIL deals.
                </p>

                <h4 className="font-semibold mt-4">Creating a Revenue Sharing Pool</h4>
                <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                  <li>Navigate to CAPS Management</li>
                  <li>Click "Create New Pool"</li>
                  <li>Select academic year, sport, and gender</li>
                  <li>Set total pool amount</li>
                  <li>Define allocation methodology</li>
                  <li>Submit for approval</li>
                </ol>

                <h4 className="font-semibold mt-4">Allocation Methodologies</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Equal Distribution:</strong> Same amount to all eligible athletes</li>
                  <li><strong>Performance-Based:</strong> Based on playing time, stats, or achievements</li>
                  <li><strong>Need-Based:</strong> Considers athlete financial circumstances</li>
                  <li><strong>Hybrid:</strong> Combination of factors</li>
                </ul>

                <h4 className="font-semibold mt-4">Title IX Considerations</h4>
                <p className="text-muted-foreground">
                  Ensure revenue sharing pools maintain proportionality with student-athlete enrollment by gender.
                  System automatically flags potential Title IX concerns.
                </p>

                <h4 className="font-semibold mt-4">Distribution Process</h4>
                <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                  <li>Review pool allocations</li>
                  <li>Verify athlete eligibility</li>
                  <li>Approve individual distributions</li>
                  <li>Process payments through business office</li>
                  <li>Track distribution status in dashboard</li>
                </ol>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="analytics">
            <AccordionTrigger className="text-lg font-semibold">
              Understanding Analytics and Trends
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Analytics Dashboard Features</h4>
                
                <h4 className="font-semibold mt-4">Deal Volume Trends</h4>
                <p className="text-muted-foreground">
                  Track deal submissions over time to identify:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Seasonal patterns (higher during recruitment periods)</li>
                  <li>Growth trends year-over-year</li>
                  <li>Sport-specific variations</li>
                </ul>

                <h4 className="font-semibold mt-4">Brand Category Analysis</h4>
                <p className="text-muted-foreground">
                  Understand which industries are most active:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Apparel and equipment</li>
                  <li>Local businesses</li>
                  <li>Social media and content</li>
                  <li>Camps and clinics</li>
                </ul>

                <h4 className="font-semibold mt-4">Gender Equity Metrics</h4>
                <p className="text-muted-foreground">
                  Monitor participation and compensation by gender:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Percentage of athletes with deals (M vs F)</li>
                  <li>Average deal value by gender</li>
                  <li>Distribution of high-value deals</li>
                  <li>Revenue sharing pool allocations</li>
                </ul>

                <h4 className="font-semibold mt-4">Using Data for Strategic Planning</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Identify underserved sports or athlete groups</li>
                  <li>Develop targeted education programs</li>
                  <li>Adjust revenue sharing allocations</li>
                  <li>Proactively address compliance trends</li>
                  <li>Benchmark against peer institutions</li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="best-practices">
            <AccordionTrigger className="text-lg font-semibold">
              Best Practices for Athletic Directors
            </AccordionTrigger>
            <AccordionContent>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li><strong>Regular Monitoring:</strong> Review dashboard weekly for emerging issues</li>
                <li><strong>Proactive Communication:</strong> Keep coaches and staff informed of policies</li>
                <li><strong>Title IX Priority:</strong> Ensure equitable opportunities and compensation</li>
                <li><strong>Education Focus:</strong> Provide ongoing NIL training for athletes and staff</li>
                <li><strong>Documentation:</strong> Maintain detailed records for audits and reviews</li>
                <li><strong>Policy Updates:</strong> Review and update NIL policies annually</li>
                <li><strong>Transparency:</strong> Share aggregate data with stakeholders appropriately</li>
                <li><strong>Collaboration:</strong> Work closely with compliance, legal, and business offices</li>
                <li><strong>Strategic Planning:</strong> Use analytics to inform resource allocation</li>
                <li><strong>Benchmark:</strong> Compare metrics with peer institutions when possible</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Card>
          <CardHeader>
            <CardTitle>Download Complete SOP</CardTitle>
            <CardDescription>
              Get the full Athletic Director Standard Operating Procedures as a PDF
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button>
              <Download className="mr-2 h-4 w-4" />
              Download PDF (32 pages)
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AthleticDirectorSOP;